'use client';

import type React from 'react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/use-toast';
import DOMPurify from 'dompurify';
import parse from 'html-react-parser';
import { Loader2, RotateCcw, Send } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { useLocation, useNavigate } from 'react-router-dom';
import ChatMessages, { type ChatMessage } from '../components/ChatMessages';
import { useAuthenticatedApi } from '../hooks/useAuthenticatedApi';
import { useSubscription } from '../hooks/useSubscription';
import type { ReferenceResponse, StatusResponse } from '../types/api';

const POLLING_INTERVAL = 2000;
const MAX_POLLING_ATTEMPTS = 30; // 1 minute maximum polling time

interface DocumentResponse {
  id: string;
  title: string;
  textData: string;
}

function isHTML(str: string): boolean {
  const htmlRegex = /<[a-z][\s\S]*>/i;
  return htmlRegex.test(str);
}

function sanitizeHTML(html: string): string {
  return DOMPurify.sanitize(html, { USE_PROFILES: { html: true } });
}

export default function CharacterDocumentChat() {
  const [document, setDocument] = useState<DocumentResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [syncChat, setSyncChat] = useState(true);
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [currentMessage, setCurrentMessage] = useState('');
  const [isSendingMessage, setIsSendingMessage] = useState(false);
  const [isClearingChat, setIsClearingChat] = useState(false);
  const [processingMessage, setProcessingMessage] = useState('');
  const [creditError, setCreditError] = useState<string | null>(null);
  const [creditDelta, setCreditDelta] = useState<number | null>(null);
  const { setSubscriptionSpend, setSubscriptionLimit } = useSubscription();
  const { apiCall } = useAuthenticatedApi();
  const location = useLocation();
  const navigate = useNavigate();
  const { toast } = useToast();

  const id = new URLSearchParams(location.search).get('id');

  const pollChatStatus = useCallback(
    async (reference: string) => {
      let attempts = 0;

      const poll = async () => {
        if (attempts >= MAX_POLLING_ATTEMPTS) {
          setIsSendingMessage(false);
          setProcessingMessage('Operation timed out. Please try again.');
          // Clear error message after 10 seconds
          setTimeout(() => {
            setProcessingMessage('');
          }, 10000);
          return;
        }

        try {
          const statusData = await apiCall<StatusResponse>(`/status?reference=${reference}`, 'GET');

          if (statusData.status === 'COMPLETE') {
            setSubscriptionSpend(statusData.creditsConsumed);
            setSubscriptionLimit(statusData.creditLimit);
          }

          switch (statusData.status) {
            case 'COMPLETE': {
              // Replace loading message with actual AI response
              setChatMessages((prev) => {
                const allButLast = prev.slice(0, -1);
                return [
                  ...allButLast,
                  {
                    user: false,
                    msg: statusData.aiResponse || '?!',
                    created: Math.floor(Date.now() / 1000),
                  },
                ];
              });

              if ('creditDelta' in statusData) {
                setCreditDelta(statusData.creditDelta!);
              }

              setIsSendingMessage(false);
              setProcessingMessage('');
              setCreditError(null); // Reset credit error on completion
              break;
            }
            case 'ERROR':
            case 'ERROR_NOT_FOUND':
            case 'ERROR_ON_WAIT_LIST': {
              // Remove loading message on error
              setChatMessages((prev) => prev.slice(0, -1));

              setIsSendingMessage(false);
              if (statusData.status === 'ERROR_NOT_FOUND') {
                toast({
                  title: 'Error',
                  description: 'Chat not found. Please try again.',
                  variant: 'destructive',
                });
              } else if (statusData.status === 'ERROR_ON_WAIT_LIST') {
                toast({
                  title: 'Error',
                  description: 'Sorry, no AI use while on the wait-list...',
                  variant: 'destructive',
                });
              } else {
                setProcessingMessage(statusData.errorDetails || 'An error occurred during the operation.');
                // Clear error message after 10 seconds
                setTimeout(() => {
                  setProcessingMessage('');
                }, 10000);
              }
              break;
            }
            case 'QUEUED':
            case 'IN_PROGRESS': {
              attempts++;
              setTimeout(poll, POLLING_INTERVAL);
              break;
            }
          }
        } catch (error) {
          console.error('Error polling status:', error);
          setIsSendingMessage(false);
          setProcessingMessage('Error checking operation status. Please try again.');
          // Clear error message after 10 seconds
          setTimeout(() => {
            setProcessingMessage('');
          }, 10000);
        }
      };

      poll();
    },
    [apiCall, setSubscriptionSpend, setSubscriptionLimit, toast]
  );

  const handleSendMessage = useCallback(
    async (messageOverride?: string) => {
      if (!document || !document.id || (!currentMessage.trim() && !messageOverride)) return;

      setIsSendingMessage(true);
      setProcessingMessage('');

      const messageToSend = messageOverride || currentMessage.trim();

      // Append the user's message to the chat
      setChatMessages((prev) => [
        ...(prev ?? []),
        {
          user: true,
          msg: messageToSend,
          created: Math.floor(Date.now() / 1000),
        },
      ]);
      setCurrentMessage('');

      // Add a loading message that will be replaced by the actual response
      setChatMessages((prev) => [
        ...prev,
        {
          user: false,
          msg: '',
          isLoading: true,
          created: Math.floor(Date.now() / 1000),
        },
      ]);

      try {
        const offline = !syncChat;
        const response = await apiCall<StatusResponse | ReferenceResponse>('/character-doc-chat', 'POST', {
          id: document.id,
          message: messageToSend,
          offline: offline,
        });

        // Check if we got a synchronous response (status is COMPLETE)
        if (!offline) {
          const r = response as StatusResponse;
          if (r.status === 'COMPLETE') {
            // Handle synchronous response
            if (r.creditsConsumed !== undefined && r.creditLimit !== undefined) {
              setSubscriptionSpend(r.creditsConsumed);
              setSubscriptionLimit(r.creditLimit);
            }

            // Replace loading message with actual AI response
            setChatMessages((prev) => {
              const allButLast = prev.slice(0, -1);
              return [
                ...allButLast,
                {
                  user: false,
                  msg: r.aiResponse || '?!',
                  created: Math.floor(Date.now() / 1000),
                },
              ];
            });

            // Update credit delta if available
            if ('creditDelta' in r) {
              setCreditDelta(r.creditDelta!);
            }

            setIsSendingMessage(false);
            setProcessingMessage('');
            setCreditError(null); // Reset credit error on completion
          } else if (r.status === 'ERROR_LACK_CREDIT') {
            setCreditError(`You've exceeded your credit limit. Used: ${r.creditsConsumed}, Limit: ${r.creditLimit}`);
          } else if (r.status === 'ERROR_ON_WAIT_LIST') {
            setCreditError('Sorry, no AI use while on the wait-list...');
          } else {
            // Handle other error statuses
            setChatMessages((prev) => prev.slice(0, -1));

            setIsSendingMessage(false);
            setProcessingMessage(r.errorDetails || 'An error occurred during the operation.');
            // Clear error message after 10 seconds
            setTimeout(() => {
              setProcessingMessage('');
            }, 10000);
          }
        } else {
          // For polling we already added a loading message above, so just start polling
          pollChatStatus((response as ReferenceResponse).reference);
        }
      } catch (err) {
        console.error('Error sending message:', err);
        // Remove loading message on error
        setChatMessages((prev) => prev.slice(0, -1));

        setIsSendingMessage(false);
        toast({
          title: 'Error',
          description: 'Failed to send message. Please try again.',
          variant: 'destructive',
        });
      }
    },
    [document, currentMessage, syncChat, apiCall, setSubscriptionSpend, setSubscriptionLimit, pollChatStatus, toast]
  );

  useEffect(() => {
    const fetchDocument = async () => {
      if (!id) {
        navigate('/documents');
        return;
      }

      try {
        const response = await apiCall<DocumentResponse>('/get-doc', 'POST', {
          id,
        });
        setDocument(response);
        setSyncChat((document?.textData ?? '').length < 10_000);

        // Fetch chat messages
        const chatResponse = await apiCall<{
          id: string;
          messages: ChatMessage[] | null;
          status?: string;
          creditsConsumed?: number;
          creditLimit?: number;
        }>('/chat', 'GET', { id });

        if (chatResponse.status === 'ERROR_LACK_CREDIT') {
          setCreditError(
            `You've exceeded your credit limit. Used: ${chatResponse.creditsConsumed}, Limit: ${chatResponse.creditLimit}`
          );
        } else {
          setChatMessages(
            chatResponse.messages ?? [
              {
                user: false,
                msg: 'Type `chat with [name]` to chat with a character.',
                created: Math.floor(Date.now() / 1000),
              },
            ]
          );
        }
      } catch (err) {
        console.error('Error fetching document or chat:', err);
        toast({
          title: 'Error',
          description: 'Error fetching document or chat',
          variant: 'destructive',
        });
        navigate('/documents');
      } finally {
        setIsLoading(false);
      }
    };

    fetchDocument();
  }, [id, apiCall, navigate, toast, document?.textData]);

  const handleCharacterSelect = useCallback(
    (characterName: string) => {
      const message = `Chat with ${characterName}`;
      setCurrentMessage(message);
      handleSendMessage(message);
    },
    [handleSendMessage]
  );

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Add this new function after the handleKeyDown function
  const handleClearChat = async () => {
    if (!document || !document.id || isClearingChat) return;

    setIsClearingChat(true);

    try {
      await apiCall('/chat', 'DELETE', { id: document.id });

      // Clear the chat messages locally
      setChatMessages([
        {
          user: false,
          msg: 'Type `chat with [name]` to chat with a character.',
          created: Math.floor(Date.now() / 1000),
        },
      ]);

      // Show success toast
      toast({
        title: 'Chat cleared',
        description: 'The conversation has been reset.',
      });
    } catch (err) {
      console.error('Error clearing chat:', err);
      toast({
        title: 'Error',
        description: 'Failed to clear the conversation. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsClearingChat(false);
    }
  };

  const renderContent = (content: string) => {
    if (isHTML(content)) {
      const sanitizedHTML = sanitizeHTML(content);
      return <div className="prose dark:prose-invert max-w-none">{parse(sanitizedHTML)}</div>;
    } else {
      return <ReactMarkdown className="prose dark:prose-invert max-w-none">{content}</ReactMarkdown>;
    }
  };

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-8 flex justify-center items-center">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (!document) {
    return <div className="container mx-auto px-4 py-8">No document found.</div>;
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <Card className="flex flex-col h-[90vh]">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>{document.title}</CardTitle>
          <Button
            variant="outline"
            size="sm"
            onClick={handleClearChat}
            disabled={isClearingChat || chatMessages.length === 0}
            title="Clear conversation"
          >
            {isClearingChat ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4" />}
            <span className="ml-2">Restart Chat</span>
          </Button>
        </CardHeader>
        <CardContent className="flex-grow flex flex-col overflow-hidden">
          <div className="mb-4 h-1/2 overflow-y-auto border rounded p-4">{renderContent(document.textData)}</div>
          <div className="border-t pt-4 flex-grow overflow-hidden flex flex-col">
            <h3 className="text-lg font-semibold mb-4">Character Chat</h3>
            {creditError && (
              <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
                <strong className="font-bold">Credit limit exceeded!</strong>
                <span className="block sm:inline"> {creditError}</span>
              </div>
            )}
            <div className="flex-grow overflow-y-auto">
              <ChatMessages messages={chatMessages} onCharacterSelect={handleCharacterSelect} />
              {processingMessage && (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="h-6 w-6 animate-spin mr-2" />
                  <span>{processingMessage}</span>
                </div>
              )}
            </div>
          </div>
        </CardContent>
        <CardFooter>
          <div className="flex flex-col w-full">
            <div id="send-msg-panel" className="flex w-full items-center space-x-2">
              <Input
                type="text"
                placeholder="Type your message here..."
                value={currentMessage}
                onChange={(e) => setCurrentMessage(e.target.value)}
                onKeyDown={handleKeyDown}
                disabled={isSendingMessage || !!creditError}
                maxLength={1000}
              />
              <Button
                onClick={() => handleSendMessage()}
                disabled={isSendingMessage || !currentMessage.trim() || !!creditError}
              >
                <Send className="h-4 w-4 mr-2" />
                Send
              </Button>
            </div>

            <div className="flex justify-between items-center mt-1">
              <div className="text-xs text-muted-foreground">{currentMessage.length}/1000 characters</div>

              {creditDelta !== null && (
                <div className="text-xs text-muted-foreground flex items-center">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="h-3 w-3 mr-1"
                  >
                    <circle cx="12" cy="12" r="10"></circle>
                    <line x1="12" y1="16" x2="12" y2="12"></line>
                    <line x1="12" y1="8" x2="12.01" y2="8"></line>
                  </svg>
                  AI responses may be inaccurate. Last credit consumed: ~{creditDelta.toFixed(2)}
                </div>
              )}
            </div>
          </div>
        </CardFooter>
      </Card>
    </div>
  );
}
